/**
 * アプリ内通知のAPIサービス（ヘッダー通知ベル用）
 *
 * メール配信停止系は services/notifications.ts に分離されている。
 */

import { fetchAuthSession } from 'aws-amplify/auth';
import { throwIfRateLimited } from '@/services/apiErrors';
import type { components } from '@/types/api-schema';

export type AppNotification = components['schemas']['AppNotification'];
export type AppNotificationListResponse =
  components['schemas']['AppNotificationListResponse'];
export type UnreadCountResponse = components['schemas']['UnreadCountResponse'];

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

async function getAuthTokenRequired(): Promise<string> {
  const session = await fetchAuthSession({ forceRefresh: false });
  const token = session.tokens?.idToken?.toString();
  if (!token) {
    throw new Error('認証トークンが見つかりません。');
  }
  return token;
}

async function throwResponseError(
  response: Response,
  fallback: string,
): Promise<never> {
  throwIfRateLimited(response);
  const error = await response.json().catch(() => ({}));
  throw new Error(error.error || fallback);
}

/** 通知一覧を取得（ユーザー別通知＋運営お知らせのマージ、新しい順） */
export async function getNotifications(
  cursor?: string,
): Promise<AppNotificationListResponse> {
  const token = await getAuthTokenRequired();
  const params = new URLSearchParams();
  if (cursor) params.set('cursor', cursor);
  const query = params.size > 0 ? `?${params.toString()}` : '';
  const response = await fetch(`${API_BASE_URL}/api/notifications${query}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    await throwResponseError(response, '通知の取得に失敗しました');
  }
  return response.json();
}

/** 未読件数を取得 */
export async function getUnreadCount(): Promise<UnreadCountResponse> {
  const token = await getAuthTokenRequired();
  const response = await fetch(
    `${API_BASE_URL}/api/notifications/unread-count`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  if (!response.ok) {
    await throwResponseError(response, '未読件数の取得に失敗しました');
  }
  return response.json();
}

/** 通知を1件既読にする（notificationId に # を含むため POST ボディで渡す） */
export async function markNotificationRead(
  notificationId: string,
): Promise<void> {
  const token = await getAuthTokenRequired();
  const response = await fetch(`${API_BASE_URL}/api/notifications/read`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ notificationId }),
  });
  if (!response.ok) {
    await throwResponseError(response, '既読の更新に失敗しました');
  }
}

/** すべての通知を既読にする */
export async function markAllNotificationsRead(): Promise<void> {
  const token = await getAuthTokenRequired();
  const response = await fetch(`${API_BASE_URL}/api/notifications/read-all`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    await throwResponseError(response, '既読の更新に失敗しました');
  }
}
